import React, { FC, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Circle, Tooltip, useMap } from "react-leaflet";
import { RED, GREEN, OPACITY, trimText } from "../RNMapView";
import { TooltipName } from "../../../../components/shared/TooltipName/TooltipName";
import { positionShapeTooltip } from "../utils/shapeTooltipAlignment";
import { ContrastName } from "../../../../components/shared/ContrastName/ContrastName";
import { getMapTooltipSizeRatio } from "../utils/getMapTooltipSizeRatio";

interface Props {
  isBooked: boolean;
  name: string;
  circle: {
    id: string;
    latitude: number;
    longitude: number;
    radius: number;
    labelAlignment?: string;
  };
  showTooltips?: boolean;
  showRoomMarkers?: boolean;
  scaleFactor?: number;
  mapReady?: boolean;
  hasContrastTooltips?: boolean;
}

const RNCircle: FC<Props> = ({
  isBooked,
  name,
  circle,
  showRoomMarkers,
  showTooltips,
  scaleFactor,
  mapReady = false,
  hasContrastTooltips = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRoomMarker, setShowRoomMarker] = useState(false);
  const circleRef = useRef<L.Circle | null>(null);
  const map = useMap();
  const labelAlignment = circle.labelAlignment || "center";
  const tooltipSizeRatio = getMapTooltipSizeRatio(scaleFactor);

  useEffect(() => {
    if (showTooltips === undefined || showRoomMarkers === undefined) {
      return;
    }

    setShowTooltip(showTooltips);
    setShowRoomMarker(showRoomMarkers);
  }, [showTooltips, showRoomMarkers]);

  // Position tooltip after map is ready, similar to BookingsFloorMap
  useEffect(() => {
    if (
      !circleRef.current ||
      !map ||
      !showTooltip ||
      showRoomMarker ||
      !mapReady ||
      !["top", "bottom"].includes(labelAlignment)
    ) {
      return;
    }

    // Use requestAnimationFrame to ensure tooltip is in DOM
    const id = requestAnimationFrame(() => {
      positionShapeTooltip(circleRef.current!, labelAlignment, map);
    });

    return () => cancelAnimationFrame(id);
  }, [
    labelAlignment,
    map,
    showRoomMarker,
    showTooltip,
    mapReady,
    circle.latitude,
    circle.longitude,
    circle.radius,
  ]);

  return (
    <div>
      <Circle
        ref={circleRef}
        center={[circle.latitude, circle.longitude]}
        radius={circle.radius}
        pathOptions={{
          color: isBooked ? RED : GREEN,
          fillColor: isBooked ? RED : GREEN,
        }}
        fillOpacity={OPACITY}
        weight={2}
      >
        {showTooltip && !showRoomMarker && (
          <Tooltip
            className={`room-permanent-tooltip ${
              labelAlignment === "center-rotated" ? "rotated" : ""
            }`}
            direction="center"
            offset={[0, 0]}
            opacity={1}
            permanent
          >
            {hasContrastTooltips ? (
              <ContrastName
                name={trimText(name, 18)}
                scaleFactor={scaleFactor || 0.7}
                sizeRatio={tooltipSizeRatio}
                isClientApp
              />
            ) : (
              <TooltipName
                name={name}
                isBooked={isBooked}
                scaleFactor={scaleFactor}
                sizeRatio={tooltipSizeRatio}
              />
            )}
          </Tooltip>
        )}
      </Circle>
    </div>
  );
};

export { RNCircle };
